import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  Clock, 
  Target, 
  AlertCircle, 
  Wifi, 
  RotateCcw, 
  BookOpen,
  CheckCircle,
  ArrowRight,
  Calendar
} from 'lucide-react';
import axios from 'axios';

interface ExamDetails {
  id: number;
  title: string;
  description: string;
  duration_minutes: number;
  max_attempts: number;
  deadline: string;
  total_questions: number;
  canAttempt: boolean;
  currentAttempt?: any;
}

const ExamInstructions: React.FC = () => {
  const { examId } = useParams<{ examId: string }>();
  const navigate = useNavigate();
  const [exam, setExam] = useState<ExamDetails | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchExamDetails();
  }, [examId]);

  const fetchExamDetails = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`/test/${examId}`);
      setExam(response.data.exam);
    } catch (error: any) {
      console.error('Error fetching exam details:', error);
      setError('Error cargando los detalles del examen');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('es-ES', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const startExam = () => {
    navigate(`/exam/${examId}/take`);
  };

  if (loading) {
    return (
      <div className="loading">
        <div className="spinner"></div>
        <p style={{ marginTop: '20px', color: 'white', fontSize: '18px' }}>
          Cargando instrucciones... 📚
        </p>
      </div>
    );
  }

  if (error || !exam) {
    return (
      <div className="container">
        <div className="card" style={{ textAlign: 'center', maxWidth: '500px', margin: '50px auto' }}>
          <h2>😔 Error</h2>
          <p>{error || 'Examen no encontrado'}</p>
          <Link to="/dashboard" className="btn btn-secondary">
            Volver al Dashboard
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="container">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: -30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8 }}
        className="title"
      >
        <h1>
          <span className="emoji">📚</span>
          ¿Qué Aprendí?
          <span className="emoji">🎯</span>
        </h1>
        <h2>ÁREA: INGLÉS CONVERSACIONAL</h2>
        <h3>TEMA: {exam.title}</h3>
      </motion.div>

      {/* Estado del examen */}
      <motion.div
        initial={{ opacity: 0, scale: 0.8 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ delay: 0.2 }}
        className="card"
        style={{ textAlign: 'center', marginBottom: '30px' }}
      >
        <div style={{ marginBottom: '20px' }}>
          <span style={{ 
            background: exam.canAttempt ? '#4ecdc4' : '#56ab2f', 
            color: 'white', 
            padding: '10px 20px', 
            borderRadius: '25px', 
            fontSize: '18px',
            fontWeight: 'bold',
            display: 'inline-block'
          }}>
            {exam.canAttempt ? '🎯 Por Realizar' : '✅ Completado'}
          </span>
        </div>
      </motion.div>

      {/* Información clave */}
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.4 }}
        className="grid grid-3"
      >
        <div className="card" style={{ textAlign: 'center' }}>
          <Calendar style={{ fontSize: '2rem', color: '#ff6b6b', marginBottom: '10px' }} />
          <h4 style={{ color: '#333', marginBottom: '10px' }}>Fecha Límite</h4>
          <p style={{ fontSize: '14px', color: '#666', margin: 0 }}>
            {exam.deadline ? formatDate(exam.deadline) : 'Sin límite de tiempo'}
          </p>
        </div>

        <div className="card" style={{ textAlign: 'center' }}>
          <RotateCcw style={{ fontSize: '2rem', color: '#4ecdc4', marginBottom: '10px' }} />
          <h4 style={{ color: '#333', marginBottom: '10px' }}>Intentos Permitidos</h4>
          <p style={{ fontSize: '18px', fontWeight: 'bold', color: '#4ecdc4', margin: 0 }}>
            {exam.max_attempts} intento{exam.max_attempts > 1 ? 's' : ''}
          </p>
        </div>

        <div className="card" style={{ textAlign: 'center' }}>
          <Clock style={{ fontSize: '2rem', color: '#ffd700', marginBottom: '10px' }} />
          <h4 style={{ color: '#333', marginBottom: '10px' }}>Duración</h4>
          <p style={{ fontSize: '18px', fontWeight: 'bold', color: '#ffd700', margin: 0 }}>
            {exam.duration_minutes} minutos
          </p>
        </div>
      </motion.div>

      {/* Información adicional */}
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.6 }}
        className="card"
      >
        <h3 style={{ color: '#333', marginBottom: '20px', textAlign: 'center' }}>
          📋 Información Adicional
        </h3>

        <div style={{ display: 'grid', gap: '15px' }}>
          <div style={{ 
            display: 'flex', 
            alignItems: 'center', 
            padding: '15px', 
            background: 'rgba(78, 205, 196, 0.1)', 
            borderRadius: '10px' 
          }}>
            <Target style={{ fontSize: '1.5rem', color: '#4ecdc4', marginRight: '15px' }} />
            <div>
              <strong>Total de preguntas: {exam.total_questions}</strong>
              <p style={{ margin: '5px 0 0 0', color: '#666', fontSize: '14px' }}>
                Demuestra todo lo que has aprendido
              </p>
            </div>
          </div>

          <div style={{ 
            display: 'flex', 
            alignItems: 'center', 
            padding: '15px', 
            background: 'rgba(255, 107, 107, 0.1)', 
            borderRadius: '10px' 
          }}>
            <Wifi style={{ fontSize: '1.5rem', color: '#ff6b6b', marginRight: '15px' }} />
            <div>
              <strong>Revisa tu conexión a internet</strong>
              <p style={{ margin: '5px 0 0 0', color: '#666', fontSize: '14px' }}>
                Asegúrate de que sea estable antes de empezar
              </p>
            </div>
          </div>

          <div style={{ 
            display: 'flex', 
            alignItems: 'center', 
            padding: '15px', 
            background: 'rgba(255, 193, 7, 0.1)', 
            borderRadius: '10px' 
          }}>
            <AlertCircle style={{ fontSize: '1.5rem', color: '#ffc107', marginRight: '15px' }} />
            <div>
              <strong>El cronómetro sigue corriendo</strong>
              <p style={{ margin: '5px 0 0 0', color: '#666', fontSize: '14px' }}>
                Una vez iniciado el test, el cronómetro seguirá corriendo ({exam.duration_minutes} minutos) 
                aunque cierres y vuelvas a abrir la página
              </p>
            </div>
          </div>

          <div style={{ 
            display: 'flex', 
            alignItems: 'center', 
            padding: '15px', 
            background: 'rgba(86, 171, 47, 0.1)', 
            borderRadius: '10px' 
          }}>
            <BookOpen style={{ fontSize: '1.5rem', color: '#56ab2f', marginRight: '15px' }} />
            <div>
              <strong>Se recomienda repasar la clase</strong>
              <p style={{ margin: '5px 0 0 0', color: '#666', fontSize: '14px' }}>
                Antes de iniciar el test de "¿Qué aprendí?"
              </p>
            </div>
          </div>
        </div>
      </motion.div>

      {/* Botones de acción */}
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.8 }}
        style={{ textAlign: 'center', marginTop: '30px' }}
      >
        {exam.canAttempt ? (
          <motion.button
            onClick={startExam}
            className="btn"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            style={{ 
              fontSize: '20px', 
              padding: '20px 40px',
              background: 'linear-gradient(45deg, #ff6b6b, #4ecdc4)'
            }}
          >
            <ArrowRight style={{ marginRight: '10px' }} />
            ¡INICIAR! 🚀
          </motion.button>
        ) : (
          <div>
            <div className="alert alert-success" style={{ marginBottom: '20px' }}>
              ✅ ¡Ya completaste este examen!
            </div>
            <Link to="/dashboard" className="btn btn-secondary">
              Volver al Dashboard
            </Link>
          </div>
        )}
      </motion.div>

      {/* Elementos decorativos */}
      <motion.div
        animate={{ 
          y: [0, -20, 0],
          rotate: [0, 5, 0]
        }}
        transition={{ 
          duration: 3,
          repeat: Infinity,
          ease: "easeInOut"
        }}
        style={{
          position: 'absolute',
          top: '10%',
          right: '5%',
          fontSize: '3rem',
          opacity: 0.6
        }}
      >
        🎓
      </motion.div>

      <motion.div
        animate={{ 
          y: [0, 20, 0],
          rotate: [0, -5, 0]
        }}
        transition={{ 
          duration: 4,
          repeat: Infinity,
          ease: "easeInOut"
        }}
        style={{
          position: 'absolute',
          bottom: '20%',
          left: '5%',
          fontSize: '2.5rem',
          opacity: 0.7
        }}
      >
        📚
      </motion.div>
    </div>
  );
};

export default ExamInstructions;
